import './header-group.scss';

import { z } from 'zod';

import { cn } from '@sola/ark-core/utils/helpers.ts';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.ts';
import { getHeaderDisplayLevel } from '@sola/utils/assembly.ts';

import { Image } from '../../atoms/image/image.tsx';
import { getBaseSizeProps } from '../../utils/text-utils.tsx';
import {
  FeatureBase,
  FeatureBaseProps,
} from '../feature-base/feature-base.tsx';
import { FeatureButtons } from '../feature-buttons/feature-buttons.tsx';

const HeaderGroupProps = FeatureBaseProps.extend({
  displayLevel: z.union([z.number(), z.literal('default')]).optional(),
  icon: IconNamesSafe.optional(),
  imagePlacement: z.enum(['left', 'right']).optional(),
  variant: z.enum(['standard', 'horizontal', 'allSide']).optional(),
  inline: z.boolean().optional(),
});
export type HeaderGroupProps = z.infer<typeof HeaderGroupProps>;

export const HeaderGroup: React.FC<HeaderGroupProps> = ({
  seoLevel = 2,
  displayLevel,
  media,
  mediaLogo,
  primaryCTA,
  secondaryCTA,
  tertiaryCTA,
  variant = 'standard',
  ...props
}) => {
  const hasAnyCtas = primaryCTA || secondaryCTA || tertiaryCTA;
  const isAllSide = variant === 'allSide';
  const hasSideLink = isAllSide ? !!hasAnyCtas : !media?.image && !!tertiaryCTA;
  const hasBaseButtons = primaryCTA || secondaryCTA;
  const headerDisplayLevel = getHeaderDisplayLevel(seoLevel);

  const featureBaseClassName = [];
  if (hasSideLink && !isAllSide) {
    featureBaseClassName.push('has-sidelink');

    if (!hasBaseButtons) {
      featureBaseClassName.push('has-sidelink--tertiary');
    }
  }

  const variantClasses = {
    default: {
      layout: 'md:flex-row',
      align: 'items-start',
      gap: 'gap-comfy-xl',
    },
    sideImage: {
      layout: 'md:flex-row',
      align: 'items-center',
      gap: 'gap-comfy-xl',
    },
    sideLink: {
      layout: 'md:flex-row',
      align: 'items-start',
      gap: 'gap-comfy-sm md:gap-comfy-xl',
    },
    sideLinks: {
      layout: 'HeaderGroup--allSide lg:flex-row',
      align: 'items-start lg:items-end',
      gap: 'gap-comfy-sm lg:gap-comfy-xl',
    },
  } as const;

  const resolvedVariant = [
    isAllSide && 'sideLinks',
    media?.image && 'sideImage',
    hasSideLink && 'sideLink',
    'default',
  ].find(Boolean) as keyof typeof variantClasses;

  const { layout, align, gap } = variantClasses[resolvedVariant];

  return (
    <div
      className={cn(
        'HeaderGroup flex flex-col justify-between',
        layout,
        align,
        gap,
      )}
    >
      <FeatureBase
        className={cn(
          'HeaderGroup__content w-full max-w-md',
          featureBaseClassName.join(' '),
        )}
        {...props}
        primaryCTA={primaryCTA}
        secondaryCTA={secondaryCTA}
        tertiaryCTA={tertiaryCTA}
        {...getBaseSizeProps(displayLevel || headerDisplayLevel)}
        media={mediaLogo}
        seoLevel={seoLevel}
        variant="standard"
        inline
      />
      {media?.image && (
        <div className="HeaderGroup__image">
          <Image
            image={media?.image}
            aspectRatio={media?.image.ratio || '1'}
            rounded="lg"
            className={cn('HeaderGroup__image-img')}
            priority={seoLevel === 1}
          />
        </div>
      )}
      {hasSideLink && (
        <div className={cn('HeaderGroup__ctas')}>
          {isAllSide ? (
            <FeatureButtons
              primaryCTA={primaryCTA}
              secondaryCTA={secondaryCTA}
              tertiaryCTA={tertiaryCTA}
              variant="default"
              className="gap-comfy-sm"
            />
          ) : (
            <FeatureButtons
              tertiaryCTA={tertiaryCTA}
              variant="stacked"
              className="md:flex-col md:items-end gap-comfy-sm"
              isLinks
              inline
            />
          )}
        </div>
      )}
    </div>
  );
};
